import i18n from "locale/i18n";
import localforage from "localforage";
import {
  getPortfolioSessionUserId,
  getLastUsedPortfolioUserId,
  recapReportComparisonDataSelector,
  currentPortfolioSelector,
  portfoliosSelector,
  portfoliosStateSelector,
  custodianSelector,
  sheetAndSectionReportNodeSelector,
  recapReportContentsDataSelector,
  reportTargetPercentageSelector,
  chartTimeRange,
  recapChartOptions,
  chartContent,
  getTickerUsingShortName,
  getTickerUsingId,
  getExchangeRate,
  formatNumberWithKuberaNumberFormatSettingsSelector,
  siteConfigSelector,
  getCharacterForNumberSeparator,
  SIMILAR_FORMAT_CURRENCIES,
  accountLinkingService,
  UNKNOWN_TICKER_SHORT_NAME,
  getTickerSymbolToPrefix
} from "../reducers/Common";
import { decompressObject, compressObject } from "../../utilities/CompressedSerialize";
import { isAppInViewMode } from "../../utilities/Common";
import DeferredPromise from "../../utilities/DeferredPromise";
import { isMobile } from "../../utilities/Location";
import {
  getPercentageValue,
  parseKuberaDateString,
  getKuberaDateString,
  MONTHS_IN_QUARTER
} from "../../utilities/Number";
import { captureError } from "../../utilities/CaptureError";
import { createSelector } from "reselect";
import clarkPersonImage from "../../assets/images/clark.png";
import loisPersonImage from "../../assets/images/lois.png";
import kentPersonImage from "../../assets/images/kent.png";
import {
  toUserTimezoneDate,
  getTodayCivilDateString,
  toCivilDateString,
  parseCivilDateString,
  addDaysToCivilDateString,
  addMonthsToCivilDateString,
  addYearsToCivilDateString,
  getCivilDaysBetween
} from "../../utilities/Timezone";

const isMobileDevice = isMobile();

export const FORCE_ERROR_TYPES = {
  ERROR_BOUNDARY: 1,
  MEMORY_ERROR: 2
};
export const WL_PLACEHOLDER_EMAIL = "client-8efb";
export const SAVE_RECAP_TO_DB = "SAVE_RECAP_TO_DB";
export const UPDATE_RECAP = "UPDATE_RECAP";
export const MIN_CHART_DATA_POINTS = 5;
const ONE_DAY_IN_MILLIS = 24 * 60 * 60 * 1000;
const ALL_RANGE_DAILY_MAX_AGE = 31 * ONE_DAY_IN_MILLIS;
const ALL_RANGE_WEEKLY_MAX_AGE = 365 * ONE_DAY_IN_MILLIS;
const ALL_RANGE_MONTHLY_MAX_AGE = 6 * 365 * ONE_DAY_IN_MILLIS;
const WEEKLY_RANGE_DAYS = 7;
const MONTHLY_RANGE_MONTHS = 1;
const QUARTERLY_RANGE_DAYS = 90;
const YEARLY_RANGE_YEARS = 1;
const CIVIL_MONTH_START_SUFFIX = "-01";
const DAYS_ELAPSED_INCLUSIVE_OFFSET = 1;
export const REHYDRATE_RECAP = "REHYDRATE_RECAP";
export const LAST_FETCH_MAX_AGE = 172800000;
export const tickersRehydratedPromise = new DeferredPromise();

export const tickerSubTypes = {
  PRECIOUS_METALS: "pm",
  HOME: "home",
  CARS: "cars",
  DOMAINS: "domains",
  PRIMARY_RESIDENCE: "primary residence",
  PRE_IPO_STOCK: "pre"
};

export const custodianTypes = {
  INVESTABLE: 0,
  NON_INVESTABLE: 1,
  CASH_IN_HAND: 2
};

export const GET_FUND_SCHEDULE_DATA_SUCCESS = "GET_FUND_SCHEDULE_DATA_SUCCESS";
export const FETCH_PORTFOLIOS_PENDING = "FETCH_PORTFOLIOS_PENDING";
export const UPDATE_FETCH_HOLDINGS_STATUS = "UPDATE_FETCH_HOLDINGS_STATUS";
export const UPDATE_ALL_CONNECTED_CUSTODIANS_REFRESH_STATUS = "UPDATE_ALL_CONNECTED_CUSTODIANS_REFRESH_STATUS";
export const FETCH_PORTFOLIOS_ERROR = "FETCH_PORTFOLIOS_ERROR";
export const SET_PORTFOLIOS = "SET_PORTFOLIOS";
export const INSERT_PORTFOLIO = "INSERT_PORTFOLIO";
export const UPDATE_PORTFOLIO = "UPDATE_PORTFOLIO";
export const DELETE_PORTFOLIO = "DELETE_PORTFOLIO";
export const UPDATE_CURRENT_PORTFOLIO = "UPDATE_CURRENT_PORTFOLIO";
export const INSERT_CUSTODIAN_BULK = "INSERT_CUSTODIAN_BULK";
export const UPDATE_CUSTODIAN_BULK = "UPDATE_CUSTODIAN_BULK";
export const DELETE_CUSTODIAN = "DELETE_CUSTODIAN";
export const DELETE_CUSTODIAN_BULK = "DELETE_CUSTODIAN_BULK";
export const DELETE_HOLDING_BULK = "DELETE_HOLDING_BULK";
export const INSERT_HOLDING_BULK = "INSERT_HOLDING_BULK";
export const INSERT_SECTION = "INSERT_SECTION";
export const INSERT_TAG = "INSERT_TAG";
export const REPLACE_TAG = "REPLACE_TAG";
export const SET_TAG_MEMBERS = "SET_TAG_MEMBERS";
export const SET_TAG_ASSIGNMENTS = "SET_TAG_ASSIGNMENTS";
export const UPDATE_TAG = "UPDATE_TAG";
export const DELETE_TAG = "DELETE_TAG";
export const UPDATE_SECTION = "UPDATE_SECTION";
export const MOVE_SECTION = "MOVE_SECTION";
export const UPDATE_SHEET = "UPDATE_SHEET";
export const MOVE_SHEET = "MOVE_SHEET";
export const UPDATE_DASHBOARD = "UPDATE_DASHBOARD";
export const CLEAR_DASHBOARD_ENTITIES_TO_UPDATE = "CLEAR_DASHBOARD_ENTITIES_TO_UPDATE";
export const BULK_CHANGE_CUSTODIAN_STAR_STATUS = "BULK_CHANGE_CUSTODIAN_STAR_STATUS";
export const BULK_CHANGE_CUSTODIAN_UPDATED_STATUS = "BULK_CHANGE_CUSTODIAN_UPDATED_STATUS";
export const DELETE_SECTION = "DELETE_SECTION";
export const DELETE_SHEET = "DELETE_SHEET";
export const INSERT_SHEET = "INSERT_SHEET";
export const INSERT_DOCUMENT = "INSERT_DOCUMENT";
export const UPDATE_DOCUMENT = "UPDATE_DOCUMENT";
export const DELETE_DOCUMENT = "DELETE_DOCUMENT";
export const SET_PROOFS = "SET_PROOFS";
export const UPSERT_PROOF = "UPSERT_PROOF";
export const REMOVE_PROOF = "REMOVE_PROOF";
export const SET_PORTFOLIO_LAST_FORCE_REFRESH_TS = "SET_PORTFOLIO_LAST_FORCE_REFRESH_TS";
export const SET_PORTFOLIO_CHANGE_DATA_LAST_FORCE_REFRESH_TS = "SET_PORTFOLIO_CHANGE_DATA_LAST_FORCE_REFRESH_TS";
export const FETCH_NET_WORTH_DATA_PENDING = "FETCH_NET_WORTH_DATA_PENDING";
export const FETCH_NET_WORTH_DATA_SUCCESS = "FETCH_NET_WORTH_DATA_SUCCESS";
export const FETCH_NET_WORTH_DATA_ERROR = "FETCH_NET_WORTH_DATA_ERROR";
export const FETCH_RECAP_DATA_ERROR = "FETCH_RECAP_DATA_ERROR";
export const FETCH_RECAP_DATA_PENDING = "FETCH_RECAP_DATA_PENDING";
export const FETCH_RECAP_DATA_SUCCESS = "FETCH_RECAP_DATA_SUCCESS";
export const FETCH_VANITY_STAT_SUCCESS = "FETCH_VANITY_STAT_SUCCESS";
export const SET_LAST_VIEWED_NETWORTH_VANITY = "SET_LAST_VIEWED_NETWORTH_VANITY";
export const FETCH_INITIAL_RECAP_DATA_PENDING = "FETCH_INITIAL_RECAP_DATA_PENDING";
export const FETCH_INITIAL_RECAP_DATA_ERROR = "FETCH_INITIAL_RECAP_DATA_ERROR";
export const SET_LINK_TYPE = "SET_LINK_TYPE";
export const RESET_PORTFOLIO_STATE = "RESET_PORTFOLIO_STATE";
export const SET_SLIDE_DIRECTION = "SET_SLIDE_DIRECTION";
export const SET_SHOW_REFRESHING = "SET_SHOW_REFRESHING";
export const PAGE_RELOADING = "PAGE_RELOADING";
export const REFRESH_CUSTODIAN_DONE = "REFRESH_CUSTODIAN_DONE";
export const SET_SECTION_UPDATED = "SET_SECTION_UPDATED";
export const REMOVE_SECTION_UPDATED = "REMOVE_SECTION_UPDATED";
export const FETCH_PORTFOLIO_CHANGE_DATA_PENDING = "FETCH_PORTFOLIO_CHANGE_DATA_PENDING";
export const FETCH_PORTFOLIO_CHANGE_DATA_SUCCESS = "FETCH_PORTFOLIO_CHANGE_DATA_SUCCESS";
export const FETCH_PORTFOLIO_CHANGE_DATA_ERROR = "FETCH_PORTFOLIO_CHANGE_DATA_ERROR";
export const FETCH_CHART_AND_CAGR_DATA_PENDING = "FETCH_CHART_AND_CAGR_DATA_PENDING";
export const FETCH_CHART_AND_CAGR_DATA_SUCCESS = "FETCH_CHART_AND_CAGR_DATA_SUCCESS";
export const UPDATE_SHOW_CHARTS_MODAL_LOADER = "UPDATE_SHOW_CHARTS_MODAL_LOADER";
export const UPDATE_RECAP_REPORT_CALCULATION_PENDING = "UPDATE_RECAP_REPORT_CALCULATION_PENDING";
export const FETCH_PERCENTAGE_ALLOCATION_DATA_PENDING = "FETCH_PERCENTAGE_ALLOCATION_DATA_PENDING";
export const FETCH_PERCENTAGE_ALLOCATION_DATA_SUCCESS = "FETCH_PERCENTAGE_ALLOCATION_DATA_SUCCESS";
export const UPDATE_RECAP_RAW_DATA_ACTION = "UPDATE_RECAP_RAW_DATA_ACTION";
export const UPDATE_PORTFOLIO_CHANGE_DATA_ACTION = "UPDATE_PORTFOLIO_CHANGE_DATA_ACTION";

export const MFA_PREFERENCES = Object.freeze({
  TOTP: "TOTP",
  SMS: "SMS",
  NONE: undefined
});
export const SIGNIN_NEXT_STEPS = Object.freeze({
  CONTINUE_SIGN_IN_WITH_MFA_SELECTION: "CONTINUE_SIGN_IN_WITH_MFA_SELECTION",
  CONTINUE_SIGN_IN_WITH_TOTP_SETUP: "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
  CONFIRM_SIGN_IN_WITH_SMS_CODE: "CONFIRM_SIGN_IN_WITH_SMS_CODE",
  CONFIRM_SIGN_IN_WITH_TOTP_CODE: "CONFIRM_SIGN_IN_WITH_TOTP_CODE",
  CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE: "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE",
  CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
  CONFIRM_SIGN_UP: "CONFIRM_SIGN_UP",
  RESET_PASSWORD: "RESET_PASSWORD",
  DONE: "DONE"
});
export const CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_SUCCESS = "CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_SUCCESS";
export const CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_PENDING = "CALCULATE_DASHBOARD_DATA_FOR_PORTFOLIO_PENDING";
export const recapChartTypes = {
  TOTALS: "totals",
  PERCENTAGE_ALLOCATION: "percentageAllocation"
};

export const isRecapCellEmpty = (cell, selectedChartType) =>
  selectedChartType === recapChartTypes.PERCENTAGE_ALLOCATION ? cell.actualValue === 0 : cell.value === 0;

export const areAllRecapCellsEmpty = (cells, selectedChartType, noOfColumns) =>
  (noOfColumns === undefined ? cells : cells.slice(0, noOfColumns)).every(cell =>
    isRecapCellEmpty(cell, selectedChartType)
  );
export const LOCK_PREFERENCE_MODES = {
  DEFAULT: {
    label: "off",
    value: "default"
  },
  IMMEDIATELY: {
    label: "lock.immediately",
    value: "immediately"
  },
  "1min": {
    label: "lock.1min",
    value: "1"
  },
  "15min": {
    label: "lock.15min",
    value: "15"
  },
  "30min": {
    label: "lock.30min",
    value: "30"
  }
};
export const THEME_SUFFIX_STR = isMobileDevice ? "" : "-desktop";
export const LOCK_PREFERENCE_STR = isMobileDevice ? "lock-preference" : "desktop-lock-preference";
export const INITIAL_FETCH_PORTFOLIOS_SUCCESS = "INITIAL_FETCH_PORTFOLIOS_SUCCESS";
export const REMOVE_CONNECTION_ERRORS = "REMOVE_CONNECTION_ERRORS";
export const FETCH_PORTFOLIOS_SUCCESS = "FETCH_PORTFOLIOS_SUCCESS";
export const chartTimeRangeGroups = {
  GROUP_BY_DAY: "groupByDay",
  GROUP_BY_WEEK: "groupByWeek",
  GROUP_BY_MONTH: "groupByMonth",
  GROUP_BY_YEAR: "groupByYear"
};
export const chartStyle = {
  DOUGHNUT: "doughnut",
  LINE: "line",
  OTHER: "other"
};
export const RECAP_CATEGORY_TYPE_INVESTABLE_ASSETS = "Investable Assets";
export const RECAP_CATEGORY_TYPE_INVESTABLE_ASSETS_WITHOUT_CASH = "Investable Assets ex Cash";
export const chartKeyParams = {
  IS_DEFAULT_CHART: "is_default_chart",
  IS_CHECKED: "is_checked"
};
export const reportPaths = {
  ASSETS: "Assets",
  DEBTS: "Debts",
  ASSETS_SHEET: "Assets/sheets",
  ASSETS_SECTION: "Assets/sections",
  ASSETS_ROW: "Assets/rows",
  DEBTS_SHEETS: "Debts/sheets",
  DEBTS_SECTION: "Debts/sections",
  DEBTS_ROW: "Debts/rows"
};
export const irrTypes = {
  COSTBASIS: "costbasis",
  CASHFLOW: "cashflow",
  HOLDING: "holding"
};
export const isCostBasisIrrTypeWithoutCost = custodian =>
  custodian?.irrType === irrTypes.COSTBASIS && custodian?.cost === null;
export const irrTimePeriods = {
  START_DATE: "start_date",
  YTD: "ytd",
  QTD: "qtd",
  YEAR: "year"
};
export const GET_CONNECTIVITY_CENTER_DATA_ERROR = "GET_CONNECTIVITY_CENTER_DATA_ERROR";
export const GET_CONNECTIVITY_CENTER_DATA_PENDING = "GET_CONNECTIVITY_CENTER_DATA_PENDING";
export const GET_CONNECTIVITY_CENTER_DATA_SUCCESS = "GET_CONNECTIVITY_CENTER_DATA_SUCCESS";
export const UPDATE_CONNECTIVITY_CENTER_DATA = "UPDATE_CONNECTIVITY_CENTER_DATA";
export const custodianSubTypes = {
  CAR: "car",
  CRYPTO: "crypto",
  DOMAIN: "domain",
  HOME: "home",
  CREDIT_CARD: "credit card",
  INVESTMENT: "investment",
  LOAN: "loan",
  CASH: "cash",
  STOCK: "stock",
  FIXED_INCOME: "fixed income",
  BOND: "bond",
  MUTUAL_FUND: "mutual fund",
  DERIVATIVE: "derivative",
  ETF: "etf",
  INSURANCE: "insurance",
  OTHER_FIXED: "other2",
  PRIMARY_RESIDENCE: "primary residence"
};
export const holdingSubTypes = Object.freeze({
  STOCK_OPTION: "stock_option",
  CONVERTIBLE_NOTE: "convertible_note",
  RSA: "rsa",
  RSU: "rsu",
  CERTIFICATE: "certificate",
  AGGREGATED_HOLDING: "aggregated holding"
});
export const custodianTaxTypes = {
  TAXABLE: 0,
  TAX_DEFERRED: 1,
  TAX_FREE: 2
};
export const userApiKeyTypes = Object.freeze({
  GENERIC: "generic",
  MCP: "mcp"
});
export const MAX_USER_API_KEYS_PER_TYPE = 5;
export const MAX_USER_MCP_API_KEYS = 10;
export const getMaxUserAPIKeysForType = type =>
  type === userApiKeyTypes.MCP ? MAX_USER_MCP_API_KEYS : MAX_USER_API_KEYS_PER_TYPE;
export const API_KEY_MASK = "xxxx";
export const getMaskedAPIKey = apiKey => (!apiKey?.keyMask === false ? `${API_KEY_MASK}${apiKey.keyMask}` : null);
export const isAPIKeyReadOnly = apiKey => Number(apiKey?.readonly) === 1;
export const filterAPIKeysByType = (apiKeys, type) =>
  (apiKeys || []).filter(apiKey => (apiKey.type || userApiKeyTypes.GENERIC) === type);
export const getDefaultAPIKeyName = (apiKeys, nameTemplate) => {
  const keys = apiKeys || [];
  const existingNames = new Set(keys.map(apiKey => apiKey.name));
  let counter = keys.length + 1;

  while (existingNames.has(nameTemplate.replace("%n%", counter))) {
    counter += 1;
  }
  return nameTemplate.replace("%n%", counter);
};
export const ADD_SCENARIO = "ADD_SCENARIO";
export const DELETE_SCENARIO = "DELETE_SCENARIO";
export const ADD_RULE = "ADD_RULE";
export const DELETE_RULE = "DELETE_RULE";
export const UPDATE_RULE = "UPDATE_RULE";
export const UPDATE_RULE_BULK = "UPDATE_RULE_BULK";
export const UPDATE_SCENARIO = "UPDATE_SCENARIO";
export const UPDATE_SCENARIO_BULK = "UPDATE_SCENARIO_BULK";
export const ADD_REPORT_PREFERENCE = "ADD_REPORT_PREFERENCE";
export const ADD_DIY_CHART = "ADD_DIY_CHART";
export const DELETE_DIY_CHART = "DELETE_DIY_CHART";
export const SET_BACKGROUND_LINK_FAILURE_DATA = "SET_BACKGROUND_LINK_FAILURE_DATA";
export const SET_POPULAR_PROVIDERS = "SET_POPULAR_PROVIDERS";
export const tickerTypes = {
  FIAT: "fiat",
  CRYPTO: "crypto",
  STOCK: "stock",
  FUND: "fund",
  BOND: "bond",
  DERIVATIVE: "derivative",
  INDEX: "index"
};

export const isFiatTicker = ticker =>
  ticker?.type === tickerTypes.FIAT && ticker?.subType !== tickerSubTypes.PRECIOUS_METALS;

export const FETCH_ARCHIVED_PORTFOLIO = "FETCH_ARCHIVED_PORTFOLIO";
export const FETCH_ARCHIVED_PORTFOLIO_SUCCESS = "FETCH_ARCHIVED_PORTFOLIO_SUCCESS";
export const FETCH_ARCHIVED_PORTFOLIO_ERROR = "FETCH_ARCHIVED_PORTFOLIO_ERROR";
export const RESET_ARCHIVED_PORTFOLIO = "RESET_ARCHIVED_PORTFOLIO";
export const DELETE_ARCHIVED_CUSTODIAN = "DELETE_ARCHIVED_CUSTODIAN";
export const SET_STATUS_CONFIG = "SET_STATUS_CONFIG";
export const SET_SITE_CONFIG = "SET_SITE_CONFIG";
export const REMOVE_STRIPE_CONNECTED_ACCOUNT = "REMOVE_STRIPE_CONNECTED_ACCOUNT";
export const SET_APP_MAINTENANCE_STATUS = "SET_APP_MAINTENANCE_STATUS";
export const SIGNOUT_USER = "SIGNOUT_USER";
export const SET_USER = "SET_USER";
export const SET_USER_ERROR = "SET_USER_ERROR";
export const SET_USER_PREFERENCES = "SET_USER_PREFERENCES";
export const SET_MFA_PREFERENCE = "SET_MFA_PREFERENCE";
export const SET_SIGNIN_REDIRECT_PATH = "SET_SIGNIN_REDIRECT_PATH";
export const SET_SIGN_IN_WITH_GOOGLE_FLOW = "SET_SIGN_IN_WITH_GOOGLE_FLOW";
export const SET_SIGN_UP_WITH_GOOGLE_FLOW = "SET_SIGN_UP_WITH_GOOGLE_FLOW";
export const FETCH_CREDIT_BALANCE_PENDING = "FETCH_CREDIT_BALANCE_PENDING";
export const UPDATE_CREDIT_BALANCE = "UPDATE_CREDIT_BALANCE";
export const FETCH_MULTIUSER_LIST_PENDING = "FETCH_MULTIUSER_LIST_PENDING";
export const SET_MULTIUSER_LIST = "SET_MULTIUSER_LIST";
export const SET_MULTIUSER_INVITATION_DATA = "SET_MULTIUSER_INVITATION_DATA";
export const SET_SHARE_MASK = "SET_SHARE_MASK";
export const SET_RECEIPTS = "SET_RECEIPTS";
export const SET_USER_OAUTH_CONNECTIONS = "SET_USER_OAUTH_CONNECTIONS";
export const SET_USER_API_KEYS = "SET_USER_API_KEYS";
export const SHOW_BREAKING_CHANGES = "SHOW_BREAKING_CHANGES";
export const ADD_USER_ACTION = "ADD_USER_ACTION";
export const LINK_PORTFOLIO_ACTION = "LINK_PORTFOLIO_ACTION";
export const OWNERSHIP_ACTION = "OWNERSHIP_ACTION";
export const BLACK_FEATURE_TRIGGERED = "BLACK_FEATURE_TRIGGERED";
export const SUBSCRIPTION_STATUS = Object.freeze({
  ACTIVE: "active",
  CANCELED: "canceled",
  PAST_DUE: "past_due",
  EXPIRED: "expired",
  TRIALING: "trialing",
  INCOMPLETE_EXPIRED: "incomplete_expired"
});
export const SUBSCRIPTION_PLANS = Object.freeze({
  TRIAL: "trial",
  MONTHLY: "monthly",
  YEARLY: "yearly",
  YEARLY_FAMILY: "yearly_family",
  YEARLY_BLACK: "yearly_black",
  YEARLY_GOLD: "yearly_gold"
});
export const BLACK_TIER_PLANS = Object.freeze([SUBSCRIPTION_PLANS.YEARLY_BLACK, SUBSCRIPTION_PLANS.YEARLY_GOLD]);
export const INITIAL_CARD_SETUP_TIMESTAMP = 1623821400;
export const API_ACCESS_TIMESTAMP = 1724220031;
export const FETCH_WL_DASHBOARD_PENDING = "FETCH_WL_DASHBOARD_PENDING";
export const FETCH_WL_DASHBOARD_SUCCESS = "FETCH_WL_DASHBOARD_SUCCESS";
export const FETCH_WL_DASHBOARD_ERROR = "FETCH_WL_DASHBOARD_ERROR";
export const WL_ADD_CLIENT = "WL_ADD_CLIENT";
export const WL_UPDATE_CLIENT = "WL_UPDATE_CLIENT";
export const WL_UPDATE_DASHBOARD_CLIENT = "WL_UPDATE_DASHBOARD_CLIENT";
export const WL_DELETE_CLIENT = "WL_DELETE_CLIENT";
export const WL_ADD_MANAGER = "WL_ADD_MANAGER";
export const WL_UPDATE_MANAGER = "WL_UPDATE_MANAGER";
export const WL_DELETE_MANAGER = "WL_DELETE_MANAGER";
export const WL_ADD_SUBUSER = "WL_ADD_SUBUSER";
export const WL_DELETE_SUBUSER = "WL_DELETE_SUBUSER";
export const WL_SET_CLIENT_CONTEXT = "WL_SET_CLIENT_CONTEXT";
export const WL_SET_ADMIN_LIST_CHARGES = "WL_SET_ADMIN_LIST_CHARGES";
export const WL_SET_FILTER_CLIENTS_BY_WL_MANAGER = "WL_SET_FILTER_CLIENTS_BY_WL_MANAGER";
export const wlUserStatus = {
  ADDED: "added",
  INVITED: "invited",
  ACCEPTED: "accepted"
};

// @INFO: This is a placeholder for the WLK payment method types
export const wlPaymentMethodType = {
  CARD: "CARD",
  BANK: "BANK",
  BACS: "BACS"
};
export const FETCH_BENEFICIARY_ERROR = "FETCH_BENEFICIARY_ERROR";
export const UPDATE_BENEFICIARY = "UPDATE_BENEFICIARY";
export const FETCH_CUSTODIAN_DETAILS = "FETCH_CUSTODIAN_DETAILS";
export const FETCH_CUSTODIAN_DETAILS_SUCCESS = "FETCH_CUSTODIAN_DETAILS_SUCCESS";
export const FETCH_CUSTODIAN_DETAILS_HISTORY_SUCCESS = "FETCH_CUSTODIAN_DETAILS_HISTORY_SUCCESS";
export const FETCH_CUSTODIAN_DETAILS_ERROR = "FETCH_CUSTODIAN_DETAILS_ERROR";
export const UPDATE_CUSTODIAN_DETAILS = "UPDATE_CUSTODIAN_DETAILS";
export const RESET_CUSTODIAN_DETAILS = "RESET_CUSTODIAN_DETAILS";
export const DELETE_CUSTODIAN_HISTORY = "DELETE_CUSTODIAN_HISTORY";
export const UPDATE_CUSTODIAN_HISTORY = "UPDATE_CUSTODIAN_HISTORY";
export const REFRESH_CUSTODIAN_HISTORY = "REFRESH_CUSTODIAN_HISTORY";
export const UPDATE_CUSTODIAN_CASHFLOW = "UPDATE_CUSTODIAN_CASHFLOW";
export const DELETE_CUSTODIAN_CASHFLOW = "DELETE_CUSTODIAN_CASHFLOW";
export const ADD_PENDING_IRR_REQUEST = "ADD_PENDING_IRR_REQUEST";
export const REMOVE_PENDING_IRR_REQUEST = "REMOVE_PENDING_IRR_REQUEST";
export const SET_INNER_DETAILS_STATE = "SET_INNER_DETAILS_STATE";
export const SET_CURRENT_STATE = "SET_CURRENT_STATE";
export const UPDATE_FUND_SCHEDULE = "UPDATE_FUND_SCHEDULE";
export const DELETE_FUND_SCHEDULE = "DELETE_FUND_SCHEDULE";
export const GET_CUSTODIAN_CHART = "GET_CUSTODIAN_CHART";
export const GET_CUSTODIAN_CHART_SUCCESS = "GET_CUSTODIAN_CHART_SUCCESS";
export const GET_CUSTODIAN_CHART_ERROR = "GET_CUSTODIAN_CHART_ERROR";
export const SET_PLUGIN_CURRENCY = "SET_PLUGIN_CURRENCY";
export const SET_LONG_EQUITY_DATA = "SET_LONG_EQUITY_DATA";
export const SAVE_PLUGIN_DATA = "SAVE_PLUGIN_DATA";
export const UPDATE_CASH_FORECAST_DURATION = "UPDATE_CASH_FORECAST_DURATION";
export const UPDATE_TICKER_DATA = "UPDATE_TICKER_DATA";
export const ADD_TICKER_INFO = "ADD_TICKER_INFO";
export const ADD_TICKER_INFO_FOR_RATE_HISTORY = "ADD_TICKER_INFO_FOR_RATE_HISTORY";
export const UPDATE_SUPPORTED_INDEXES = "UPDATE_SUPPORTED_INDEXES";
export const SHOW_TOAST = "SHOW_TOAST";
export const DISMISS_TOAST = "DISMISS_TOAST";
export const UPDATE_TOAST = "UPDATE_TOAST";
export const toastType = {
  UNDO: "UNDO",
  SYNC_ERROR: "SYNC_ERROR",
  SYNC_RESUMING: "SYNC_RESUMING",
  GENERIC_ERROR: "GENERIC_ERROR",
  TIP: "TIP",
  SYNC_BACKLOG: "SYNC_BACKLOG"
};

// possible configurations for each rule
export const planningVariables = {
  ASSET_TYPE: "asset_type",
  DEBT_TYPE: "debt_type",
  RATE_PER_YEAR: "rate_per_year",
  RATE_PER_YEAR_WITH_TAX: "rate_per_year_with_tax",
  GROWTH_RATE: "growth_rate",
  PERCENTAGE: "percentage",
  REVISED_PERCENTAGE: "revised_percentage",
  ASSET_ID: "asset_id",
  DEBT_ID: "debt_id",
  AMOUNT: "amount",
  AMOUNT_WITH_TAX: "amount_with_tax",
  AMOUNT_WITH_TAX_COST: "amount_with_tax_cost",
  COST_WITH_TAX: "cost_with_tax",
  EXPECTED_AMOUNT: "expected_amount",
  DATE_AGE: "date_age",
  DATE_AGE_YEAR: "date_age_year",
  DATE_AGE_REVISED: "date_age_revised",
  NO_DUPLICATE: "no_duplicate",
  MULTIPLE_DATES: "multi_date",
  TICKER_ID: "ticker_id",
  TICKER_ID_OBJ: "ticker_id_obj",
  QUANTITY: "quantity",
  MONTHS: "months",
  YEARS: "years",
  REPEAT: "repeat",
  NEW_ASSET: "new_asset",
  NEW_DEBT: "new_debt",
  NEW_INCOME: "new_income",
  NEW_EXPENSE: "new_expense",
  META: "meta",
  TICKER_NAME: "ticker_name",
  TAX: "tax",
  TAX_DEDUCTION_AMOUNT: "tax_deduction",
  TAX_DEDUCTION_RATE: "tax_deduction_rate",
  VESTING_SCHEDULE: "vesting_schedule",
  AUTO_AMOUNT_WITH_TAX: "auto_amount_with_tax",
  LIQUIDATION_RATE_WITH_TAX_COST: "liquidation_rate_with_tax_cost",
  CAPITAL_SCHEDULE: "capital_schedule",
  COST_FOR_TAX: "cost_for_tax",
  IS_INCREASE: "is_increase"
};
export const planningAssetTypes = {
  all: {
    label: "All",
    key: "all"
  },
  investable: {
    label: "Investable Assets",
    key: "investable"
  },
  cash: {
    label: "Cash",
    key: "cash"
  },
  investments: {
    label: "Investments",
    key: "investments"
  },
  stocks: {
    label: "Stocks",
    key: "stocks"
  },
  bonds: {
    label: "Bonds",
    key: "bonds"
  },
  cash_equivalents: {
    label: "Cash Equivalents",
    key: "cash_equivalents"
  },
  funds: {
    label: "Funds",
    key: "funds"
  },
  derivatives: {
    label: "Derivatives",
    key: "derivatives"
  },
  metals: {
    label: "Precious Metals",
    key: "metals"
  },
  crypto: {
    label: "Crypto",
    key: "crypto"
  },
  homes: {
    label: "Real Estate",
    key: "homes"
  },
  vehicles: {
    label: "Vehicles",
    key: "vehicles"
  },
  other: {
    label: "Miscellaneous",
    key: "other"
  },
  privateEquity: {
    label: "Private Equity",
    key: "privateEquity"
  },
  privateDebt: {
    label: "Private Debt",
    key: "privateDebt"
  },
  commodities: {
    label: "Commodities",
    key: "commodities"
  },
  venture: {
    label: "Venture",
    key: "venture"
  },
  artAndCollectibles: {
    label: "Art & Collectibles",
    key: "artAndCollectibles"
  },
  taxable: {
    label: "Taxable",
    key: "taxable"
  },
  taxDeferred: {
    label: "Tax Deferred",
    key: "taxDeferred"
  },
  taxFree: {
    label: "Tax Free",
    key: "taxFree"
  }
};
// set the frequency for repeating rules
export const repeatFrequency = {
  NO_REPEAT: "no_repeat",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
  BI_ANNUALLY: "bi_annually",
  YEARLY: "yearly"
};
export const timeRanges = [
  chartTimeRange.WEEKLY,
  chartTimeRange.MONTHLY,
  chartTimeRange.QUARTERLY,
  chartTimeRange.YEARLY,
  chartTimeRange.ALL
];
export const OVERRIDE_INITIAL_CC_SETUP_LIST = [
  "kpnv8523@gmail.com",
  "kpnavaneetha@gmail.com",
  "sk11985235@gmail.com",
  "kpnva98@gmail.com",
  "testkubera1@gmail.com",
  "testkubera2@gmail.com",
  "testkubera12345@gmail.com",
  "unclescrooge31@gmail.com",
  "jonsnowappleseed@gmail.com",
  "cm.layouttest@gmail.com",
  "manojmarathayil@gmail.com"
];

// Tree chart defaults
export const PORTFOLIO_TREE_SLIDES_DUMMY_DATA1 = [
  {
    name: "Clark",
    netWorth: "$6.992M",
    defaultPicture: clarkPersonImage
  },
  {
    name: "Lois",
    netWorth: "$7.147M",
    defaultPicture: loisPersonImage
  },
  {
    name: "Kent & Co. LLC",
    netWorth: "$5.5M",
    defaultPicture: kentPersonImage
  },
  {
    name: "Advisor Managed",
    netWorth: "$6.12M"
  },
  {
    name: "Trust",
    netWorth: "$2.1M"
  }
];

export const PORTFOLIO_TREE_SLIDES_DUMMY_DATA2 = [
  {
    name: "Family",
    netWorth: "$27.859M"
  },
  {
    name: "Clark",
    netWorth: "$15.552M",
    defaultPicture: clarkPersonImage
  },
  {
    name: "Lois",
    netWorth: "$12.307M",
    defaultPicture: loisPersonImage
  },
  {
    name: "Advisor Managed (50%)",
    netWorth: "$6.12M",
    borderColor: "#008DF1"
  },
  {
    name: "Kent & Co. LLC",
    netWorth: "$5.5M",
    defaultPicture: kentPersonImage
  },
  {
    name: "Trust",
    netWorth: "$2.1M"
  },
  {
    name: "Advisor Managed (50%)",
    netWorth: "$6.12M",
    borderColor: "#008DF1"
  }
];

// Config constants for TreeChart
export const CHART_LEFT_MARGIN = 24;
export const ISOLATED_NODES_VERTICAL_GAP = 50; // Gap between isolated nodes vertically
export const CLUSTER_VERTICAL_GAP = 50; // Gap between linked clusters vertically
export const ISOLATED_TO_CLUSTER_GAP = 50; // Reduced gap between isolated nodes collection and cluster collection
export const LINKED_CLUSTER_SPACING_REDUCTION = 1; // Factor to reduce spacing in linked clusters (0.5 = 50% of original)
export const LINKED_CLUSTER_SPACING_REDUCTION_WITHOUT_DESCRIPTION = 0.88; // Factor to reduce spacing in linked clusters with description eg. (0.5 = 50% of original)
export const CHART_VERTICAL_PADDING = 80; // Reduced padding for top and bottom of chart
export const LINKS_TOP_OFFSET = 9; // Offset for links to be aligned with the chart
export const BOTTOM_ADDITIONAL_PADDING = 20; // Additional padding at the bottom of the chart

// TreeNode dimensions config
export const NODE_MIN_WIDTH = 130; // Set to a number to enforce minimum width, or null to use calculated width
export const NODE_MIN_HEIGHT = 100; // Set to a number to enforce minimum height, or null to use calculated height
export const COMPACT_VIEW_NODE_MIN_HEIGHT = 50; // Set to a number to enforce minimum width, or null to use calculated width
export const NODE_DESCENDANT_MIN_HEIGHT = 80; // Set to a number to enforce minimum height, or null to use calculated height
export const LABEL_X_PADDING = 10;
export const LABEL_TOP_PADDING = 14;
export const LABEL_BOTTOM_PADDING = 22;
export const PADDING_OPTION_ICON = 30;
export const OPTION_ICON_WIDTH = 30;
export const OPTION_ICON_HEIGHT = 30;

// Methods

// Debounces clear scheduling so a burst of dispatches in one tick produces only
// one rAF callback. rAF fires after React's render commit, so the consumer reads
// the accumulated list before the slot is cleared.
let dashboardClearScheduled = false;
const scheduleDashboardEntitiesClear = dispatch => {
  if (dashboardClearScheduled === true) {
    return;
  }
  dashboardClearScheduled = true;
  const fire = () => {
    dashboardClearScheduled = false;
    dispatch({ type: CLEAR_DASHBOARD_ENTITIES_TO_UPDATE });
  };
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(fire);
  } else {
    setTimeout(fire, 16);
  }
};

export const updateDashboardAction = updatedEntitiesArray => dispatch => {
  dispatch({
    type: UPDATE_DASHBOARD,
    updatedEntitiesArray
  });
  scheduleDashboardEntitiesClear(dispatch);
};

export const clearDashboardEntitiesToUpdateAction = () => ({
  type: CLEAR_DASHBOARD_ENTITIES_TO_UPDATE
});

export const setPageReloadingAction = bool => {
  return dispatch => {
    dispatch({
      type: PAGE_RELOADING,
      isLoading: bool
    });
  };
};

export const setPortfoliosAction = (portfolios, showNonEmptyAsCurrent = false) => ({
  type: SET_PORTFOLIOS,
  portfolios,
  showNonEmptyAsCurrent
});

export const resetPortfolioStateAction = () => ({
  type: RESET_PORTFOLIO_STATE
});

export const defaultTickerSelector = state => {
  const portfolio = currentPortfolioSelector(state);
  return getTickerUsingShortName(state, portfolio.currency);
};

export const defaultCustodianTickerSelector = (state, portfolioCurrency) => {
  const portfolioTicker = getTickerUsingShortName(state, portfolioCurrency);
  return isFiatTicker(portfolioTicker) ? portfolioTicker : getTickerUsingShortName(state, "USD");
};

export const getAccountLinkingService = linkType => {
  switch (linkType) {
    case 1:
      return "yodlee";
    case 2:
      return "plaid";
    case 3:
      return "zabo";
    case 4:
      return "saltedge";
    case 5:
      return "saltedge_eu";
    case 6:
      return "zillow";
    case 7:
      return "estibot";
    case 8:
      return "vinaudit";
    case 9:
      return "zerion";
    case 10:
      return "crypto_oauth";
    case 11:
      return "crypto_api";
    case 12:
      return "flinks_banking";
    case 13:
      return "flinks_investment";
    case 14:
      return "akahu";
    case 15:
      return "lean";
    case 16:
      return "finicity";
    case 17:
      return "snaptrade";
    case 18:
      return "mx";
    case 19:
      return "kubera_portfolio";
    case 20:
      return "in_house_oauth";
    case 21:
      return "manual_account";
    case 23:
      return "akoya";
    case 24:
      return "ai_appraiser";
    default:
      return linkType;
  }
};

export const getAccountLinkTypeFromProviderId = providerId => {
  const serviceMap = {
    yodlee: accountLinkingService.YODLEE,
    plaid: accountLinkingService.PLAID,
    zabo: accountLinkingService.ZABO,
    saltedge: accountLinkingService.SALTEDGE,
    saltedge_eu: accountLinkingService.SALTEDGE_EU,
    zillow: accountLinkingService.ZILLOW,
    estibot: accountLinkingService.DOMAINS,
    vinaudit: accountLinkingService.CARS,
    zerion: accountLinkingService.ZERION,
    crypto_oauth: accountLinkingService.IN_HOUSE_CRYPTO_OAUTH,
    crypto_api: accountLinkingService.IN_HOUSE_CRYPTO_API,
    flinks_banking: accountLinkingService.FLINKS_BANKING,
    flinks_investment: accountLinkingService.FLINKS_INVESTMENT,
    akahu: accountLinkingService.AKAHU,
    lean: accountLinkingService.LEAN,
    finicity: accountLinkingService.FINICITY,
    snaptrade: accountLinkingService.SNAPTRADE,
    mx: accountLinkingService.MX,
    kubera_portfolio: accountLinkingService.KUBERA_PORTFOLIO,
    in_house_oauth: accountLinkingService.IN_HOUSE_OAUTH,
    manual_connected: accountLinkingService.MANUAL_CONNECTED,
    akoya: accountLinkingService.AKOYA,
    ai_appraiser: accountLinkingService.AI_APPRAISER
  };
  return serviceMap[providerId] || null;
};

export const isZaboToInHouseApiCandidate = linkProviderId => {
  if (!linkProviderId === true) {
    return false;
  }
  return [
    "binance",
    "binanceus",
    "bitcoin",
    "bitcoincash",
    "bitfinex",
    "bitstamp",
    "bittrex",
    "bybit",
    "cardano",
    "celsius",
    "coinbaseprime",
    "coinbasepro",
    "cryptocom",
    "dogecoin",
    "ethereumclassic",
    "ftx",
    "ftxus",
    "gateio",
    "hitbtc",
    "huobi",
    "kraken",
    "kucoin",
    "liquid",
    "litecoin",
    "okcoin",
    "poloniex",
    "ripple",
    "stellar",
    "tezos",
    "trezor",
    "ledger",
    "xpub-key",
    "xpubbitcoin"
  ].includes(linkProviderId.toLowerCase());
};

export const isZaboToInHouseOauthCandidate = linkProviderId => {
  if (!linkProviderId === true) {
    return false;
  }
  return ["coinbase", "gemini"].includes(linkProviderId.toLowerCase());
};

export const getExchangeRateDetails = (tickerId, rate, forDate = null, hasUserUpdatedExchangeRate = false) => {
  const date = !forDate === true ? toUserTimezoneDate() : forDate;
  const dateString = `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(
    -2
  )}`;
  if (hasUserUpdatedExchangeRate) {
    return JSON.stringify({ tickerId: tickerId, rate: rate, date: dateString, u: 1 });
  }
  return JSON.stringify({ tickerId: tickerId, rate: rate, date: dateString });
};

export const getExchangeRateDetailsForHistoryEntry = (state, historyEntry, currency) => {
  try {
    return JSON.parse(historyEntry.valueExchangeRate);
  } catch (error) {
    const rate = getExchangeRate(state, getTickerUsingId(state, historyEntry.tickerId).shortName, currency);
    const exchangeRateDetails = getExchangeRateDetails(getTickerUsingId(state, historyEntry.tickerId).id, rate);
    return JSON.parse(exchangeRateDetails);
  }
};

export const custodiansLinkedToSameAccountSelector = createSelector(
  [state => state, (...args) => args[1], (...args) => args[2], (...args) => args[3], (...args) => args[4]],
  (state, custodianId, excludeChildren = true, excludeHidden = false, isRemoveFlow = false) => {
    const portfolios = portfoliosSelector(state);
    const custodian = custodianSelector(state, custodianId);
    let custodians = [];
    let portfolioCustodiansMap = {};
    let custodianPortfolioMap = {};

    if (!portfolios === true || !custodian === true) {
      return { custodians, custodianPortfolioMap, portfolioCustodiansMap };
    }

    for (const portfolio of portfolios) {
      let matchingCustodians = [];
      if (isRemoveFlow) {
        matchingCustodians = portfolio.details.custodian.filter(
          temp =>
            temp.linkContainer !== "nft" &&
            !temp.linkProviderAccountId === false &&
            temp.linkProviderAccountId === custodian.linkProviderAccountId &&
            !temp.linkProviderId === false &&
            temp.linkProviderId === custodian.linkProviderId
        );
      } else {
        matchingCustodians = portfolio.details.custodian.filter(
          temp =>
            !temp.linkProviderAccountId === false &&
            temp.linkProviderAccountId === custodian.linkProviderAccountId &&
            !temp.linkProviderId === false &&
            temp.linkProviderId === custodian.linkProviderId
        );
      }

      if (excludeChildren === true) {
        matchingCustodians = matchingCustodians.filter(item => !item.parentId === true);
      }
      if (excludeHidden === true) {
        matchingCustodians = matchingCustodians.filter(item => item.hidden === 0);
      }
      if (matchingCustodians.length > 0) {
        for (const item of matchingCustodians) {
          custodianPortfolioMap[item.id] = portfolio.id;
        }
        portfolioCustodiansMap[portfolio.id] = matchingCustodians;
        custodians = matchingCustodians;
      }
    }
    return { custodians, portfolioCustodiansMap, custodianPortfolioMap };
  }
);

export const getRecapWorkerConsts = () => {
  return {
    UPDATE_RECAP,
    SAVE_RECAP_TO_DB,
    RECAP_DATA_STORAGE_KEY: getRecapDataStorageKey()
  };
};

export const getLineChartTitleSelector = (state, selectedChartOptions, reportPath, reportName, reportId) => {
  if (selectedChartOptions === recapChartOptions.SHEETS_AND_SECTIONS.id) {
    if (reportPath === "Assets/sheets" || reportPath === "Debts/sheets") {
      return `${reportName} (Sheet)`;
    } else if (reportPath === "Assets/sections" || reportPath === "Debts/sections") {
      const parentNode = sheetAndSectionReportNodeSelector(state, reportId, chartTimeRange.TODAY, true);
      return `${parentNode.name} / ${reportName}`;
    }
  }
  return reportName;
};

export const getRecommendationCountForAChartSelector = createSelector(
  [state => state, (...args) => args[1], (...args) => args[2], (...args) => args[3]],
  (state, reportId, chartContentType, percentages) => {
    try {
      let reportData;
      let recommendationCount = 0;
      // shouldCompareAgainstInvestableAssets
      if (chartContentType === "contents") {
        reportData = recapReportContentsDataSelector(state, reportId);
      } else if (chartContentType === "reports") {
        reportData = recapReportComparisonDataSelector(state, reportId);
      } else if (chartContentType === chartContent.CONTENTS_GROUPED_BY_SHEETS_AND_SECTION) {
        reportData = recapReportContentsDataSelector(state, reportId, true);
      } else if (chartContentType === chartContent.INVESTABLE_ASSETS_GROUPED_BY_SECTION) {
        reportData = recapReportContentsDataSelector(state, reportId, false, true);
      } else if (chartContentType === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SHEETS_AND_SECTION) {
        reportData = recapReportContentsDataSelector(state, reportId, false, false, true);
      } else if (chartContentType === chartContent.INVESTABLE_ASSETS_WITHOUT_CASH_GROUPED_BY_SECTION) {
        reportData = recapReportContentsDataSelector(state, reportId, false, false, false, true);
      } else if (chartContentType === chartContent.ASSETS_GROUPED_BY_SECTIONS) {
        reportData = recapReportContentsDataSelector(state, reportId, false, false, false, false, true);
      }

      if (reportData && reportData.contents) {
        for (const content of reportData.contents) {
          const actualPercentage = getPercentageValue(content.value, reportData.total, false, 2);
          const storedTargetPrecentage = reportTargetPercentageSelector(
            state,
            reportId,
            chartContentType,
            content.id || content.name
          );
          if (
            storedTargetPrecentage !== null &&
            storedTargetPrecentage !== undefined &&
            storedTargetPrecentage !== "" &&
            (storedTargetPrecentage <= actualPercentage - 5 || storedTargetPrecentage >= actualPercentage + 5)
          ) {
            recommendationCount++;
          }
        }
      }
      return recommendationCount;
    } catch (e) {
      console.log("e", e);
    }
  }
);

export const getYTDStartDateString = () =>
  `${parseCivilDateString(getTodayCivilDateString()).year}-01${CIVIL_MONTH_START_SUFFIX}`;

export const getQTDStartDateString = () => {
  const { year, month } = parseCivilDateString(getTodayCivilDateString());
  const quarterStartMonth = Math.floor((month - 1) / MONTHS_IN_QUARTER) * MONTHS_IN_QUARTER + 1;
  return `${year}-${String(quarterStartMonth).padStart(2, "0")}${CIVIL_MONTH_START_SUFFIX}`;
};

export const getYTDStartDate = () => parseKuberaDateString(getYTDStartDateString());

export const getRecapDataStorageKey = portfolioUserId => {
  const userId = portfolioUserId || getPortfolioSessionUserId();
  try {
    return window.__kuberaStorageKeys.constructRecapDataStorageKey(userId);
  } catch (err) {
    return `RECAP_DATA_INITIAL_LOAD_CHUNK_KEY-${userId}`;
  }
};

/************************ Handling recap persisted data fetch from localforage *****************************/
const IDB_READ_TIMEOUT_MS = 5000;
// Reads that aren't holding the boot gate can afford to wait out a busy
// IndexedDB rather than discard data the app has no other copy of.
const POST_BOOT_IDB_READ_TIMEOUT_MS = 30000;

// A hung IndexedDB request (post-crash lock/corruption) never settles — it
// neither resolves nor rejects — which would hold the boot gate forever. Bound
// every cache read so boot proceeds without hydration and the API fetch fills in.
const boundIdbRead = (readPromise, timeoutMs) =>
  new Promise(resolve => {
    const timeoutId = setTimeout(() => resolve(null), timeoutMs);
    readPromise
      .then(result => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch(() => {
        clearTimeout(timeoutId);
        resolve(null);
      });
  });

const getItemFromEarlyReadOrLocalForage = (key, timeoutMs = IDB_READ_TIMEOUT_MS) => {
  const earlyRead = window.__kuberaEarlyIDBReads?.take?.(key);
  const read = earlyRead ? earlyRead.catch(() => localforage.getItem(key)) : localforage.getItem(key);
  return boundIdbRead(read, timeoutMs);
};

const loadRecapDataForPortfolio = async (portfolioId, chunkKeyForTimeRanges) => {
  const timeRangeEntries = Object.entries(chunkKeyForTimeRanges?.data || {});
  const compressedDataForTimeRanges = await Promise.all(
    timeRangeEntries.map(([, chunkKeys]) => reconstructCompressedData(chunkKeys))
  );
  // A subset still reads as "recap is available" downstream, which suppresses both
  // the loader and the fetch that would repair it, so anything short of every time
  // range has to look like a cache miss.
  if (!timeRangeEntries.length || compressedDataForTimeRanges.some(compressedData => !compressedData)) {
    return null;
  }
  const recapDataForPortfolio = {};
  timeRangeEntries.forEach(([timeRange], index) => {
    recapDataForPortfolio[timeRange] = compressedDataForTimeRanges[index];
  });
  return { portfolioId, recapData: { data: recapDataForPortfolio, currency: chunkKeyForTimeRanges?.currency } };
};

const getRecapDataFromLocalForage = key => {
  try {
    return getItemFromEarlyReadOrLocalForage(getRecapDataStorageKey(key))
      .then(async data => {
        if (!data) return null;
        const portfolioEntries = await Promise.all(
          Object.entries(data).map(([portfolioId, chunkKeyForTimeRanges]) =>
            loadRecapDataForPortfolio(portfolioId, chunkKeyForTimeRanges).catch(error => {
              console.error("Error loading data from LocalForage:", error);
              return null;
            })
          )
        );
        const recapDataForAllPortfolios = {};
        for (const portfolioEntry of portfolioEntries) {
          if (portfolioEntry) {
            recapDataForAllPortfolios[portfolioEntry.portfolioId] = portfolioEntry.recapData;
          }
        }
        return Object.keys(recapDataForAllPortfolios).length > 0 ? recapDataForAllPortfolios : null;
      })
      .catch(error => {
        console.error("Error loading data from LocalForage:", error);
        return null;
      });
  } catch (err) {
    return Promise.resolve(null);
  }
};
const getRecapPortfolioSessionUserId = () => {
  return window.portfolioUserId || getPortfolioSessionUserId() || getLastUsedPortfolioUserId();
};
const recapDataFromLocalForagePromise = getRecapDataFromLocalForage(getRecapPortfolioSessionUserId());
export const loadRecapDataFromLocalForage = () => {
  return async dispatch => {
    if (isAppInViewMode()) return;
    const recapDataForAllPortfolios = await recapDataFromLocalForagePromise;
    if (!recapDataForAllPortfolios) return;
    dispatch({
      type: REHYDRATE_RECAP,
      payload: { ...recapDataForAllPortfolios }
    });
  };
};

export const reconstructCompressedData = async (chunkKeys, timeoutMs) => {
  try {
    if (!chunkKeys) {
      return null;
    }

    // Retrieve and combine chunks
    const keys = Array.from(chunkKeys);
    const chunks = await Promise.all(keys.map(key => getItemFromEarlyReadOrLocalForage(key, timeoutMs)));
    const invalidChunkIndex = chunks.findIndex(chunk => !(chunk instanceof Uint8Array));
    if (invalidChunkIndex !== -1) {
      console.error(`Chunk with key ${keys[invalidChunkIndex]} is not a Uint8Array`);
      return;
    }
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedData = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      combinedData.set(chunk, offset);
      offset += chunk.length;
    }
    return combinedData;
  } catch (error) {
    console.error("Error reconstructing data:", error);
  }
};

export const reconstructData = async chunkKeys => {
  const combinedData = await reconstructCompressedData(chunkKeys, POST_BOOT_IDB_READ_TIMEOUT_MS);
  if (!combinedData) {
    return combinedData;
  }
  try {
    return decompressObject(combinedData);
  } catch (error) {
    console.error("Error reconstructing data:", error);
  }
};

export const rehydrateRecapData = portfolioUserId => async dispatch => {
  if (getRecapPortfolioSessionUserId() !== portfolioUserId) {
    return;
  }

  const recapDataForAllPortfolios = await recapDataFromLocalForagePromise;
  if (!recapDataForAllPortfolios) return;
  dispatch({
    type: REHYDRATE_RECAP,
    payload: { ...recapDataForAllPortfolios }
  });
};

export function saveRecapDataMapInMainThread(key, dataMap) {
  try {
    return localforage
      .setItem(key, compressObject(dataMap))
      .then(() => {
        console.log("saveRecapDataMap save successful");
      })
      .catch(e => {
        console.log("saveRecapDataMap save failed", e);
      });
  } catch (e) {
    console.log("saveRecapDataMap failed", e);
    return Promise.resolve();
  }
}

export const getPortfoliosDataStorageKey = portfolioUserId => {
  const userId = portfolioUserId || getPortfolioSessionUserId();
  try {
    return window.__kuberaStorageKeys.constructPortfoliosDataStorageKey(userId);
  } catch (err) {
    return `portfolios-${userId}-v64`;
  }
};

/************************ Handling portfolios persisted data fetch from localforage *****************************/

const getPortfoliosDataFromLocalForage = key => {
  try {
    return getItemFromEarlyReadOrLocalForage(getPortfoliosDataStorageKey(key))
      .then(data => {
        // Tolerate compressed entries written before raw storage shipped
        // (old app versions and a not-yet-updated service worker).
        return data instanceof Uint8Array ? decompressObject(data) : data;
      })
      .catch(error => {
        console.error("Error loading data from LocalForage:", error);
        return null;
      });
  } catch (err) {
    return Promise.resolve(null);
  }
};
export const getPortfoliosStorePortfolioSessionUserId = () => {
  return window.portfolioUserId || getPortfolioSessionUserId() || getLastUsedPortfolioUserId();
};
const portfoliosDataFromLocalForagePromise = getPortfoliosDataFromLocalForage(
  getPortfoliosStorePortfolioSessionUserId()
);
export const loadPortfoliosDataFromLocalForage = () => {
  return async (dispatch, getState) => {
    if (isAppInViewMode()) return;
    const portfoliosData = await portfoliosDataFromLocalForagePromise;
    if (!portfoliosData) return;
    await tickersRehydratedPromise;
    // Live data wins: a slow cache read must not overwrite portfolios that a
    // network fetch has already put in the store.
    if (portfoliosStateSelector(getState())?.portfolios) return;
    if (getState().tickers.tickerData) {
      dispatch(setPortfoliosAction(portfoliosData, isMobileDevice));
    }
  };
};

export const rehydratePortfoliosData = portfolioUserId => async (dispatch, getState) => {
  if (getPortfoliosStorePortfolioSessionUserId() !== portfolioUserId) {
    return;
  }

  const portfoliosData = await portfoliosDataFromLocalForagePromise;
  if (!portfoliosData) return;
  await tickersRehydratedPromise;
  if (portfoliosStateSelector(getState())?.portfolios) return;
  if (getState().tickers.tickerData) {
    dispatch(setPortfoliosAction(portfoliosData, isMobileDevice));
  }
};

export const sortPortfolio = portfolio => {
  if (!portfolio.details) return;
  portfolio.details.custodian.sort((a, b) => (!a.sortKey === true ? 0 : a.sortKey.localeCompare(b.sortKey)));
  portfolio.details.section.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  portfolio.details.sheet.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
};

export const getChartDataFromResponse = (
  responseData,
  dataPointsKey,
  shouldUseLatestKeys,
  isReportsData,
  networthChartStartDate
) => {
  const chartData = {};
  const dailyData = shouldUseLatestKeys
    ? responseData[chartTimeRange.DAILY] || []
    : responseData[chartTimeRangeGroups.GROUP_BY_DAY] || [];
  const weeklyData = shouldUseLatestKeys
    ? responseData[chartTimeRange.WEEKLY] || []
    : responseData[chartTimeRangeGroups.GROUP_BY_WEEK] || [];
  const monthlyData = shouldUseLatestKeys
    ? responseData[chartTimeRange.MONTHLY] || []
    : responseData[chartTimeRangeGroups.GROUP_BY_MONTH] || [];
  const yearlyData = shouldUseLatestKeys
    ? responseData[chartTimeRange.YEARLY] || []
    : responseData[chartTimeRangeGroups.GROUP_BY_YEAR] || [];

  const todayDateString = getTodayCivilDateString();
  const isOnOrAfter = startDateString => item => toCivilDateString(item.date) >= startDateString;

  // Calcluate last week data
  if (dailyData[dataPointsKey].length >= MIN_CHART_DATA_POINTS) {
    chartData[chartTimeRange.WEEKLY] = { ...dailyData };
    chartData[chartTimeRange.WEEKLY][dataPointsKey] = dailyData[dataPointsKey].filter(
      isOnOrAfter(addDaysToCivilDateString(todayDateString, -WEEKLY_RANGE_DAYS))
    );
  } else {
    chartData[chartTimeRange.WEEKLY] = { ...dailyData };
  }

  // Calculate last month data
  chartData[chartTimeRange.MONTHLY] = { ...dailyData };
  chartData[chartTimeRange.MONTHLY][dataPointsKey] = dailyData[dataPointsKey].filter(
    isOnOrAfter(addMonthsToCivilDateString(todayDateString, -MONTHLY_RANGE_MONTHS))
  );

  // Calculate last quarter data
  if (weeklyData?.[dataPointsKey]?.length >= MIN_CHART_DATA_POINTS) {
    chartData[chartTimeRange.QUARTERLY] = { ...weeklyData };
    chartData[chartTimeRange.QUARTERLY][dataPointsKey] = weeklyData[dataPointsKey].filter(
      isOnOrAfter(addDaysToCivilDateString(todayDateString, -QUARTERLY_RANGE_DAYS))
    );
  } else {
    chartData[chartTimeRange.QUARTERLY] = { ...dailyData };
  }

  // Calculate last year data
  if (weeklyData?.[dataPointsKey]?.length >= MIN_CHART_DATA_POINTS) {
    chartData[chartTimeRange.YEARLY] = { ...weeklyData };
    chartData[chartTimeRange.YEARLY][dataPointsKey] = weeklyData[dataPointsKey].filter(
      isOnOrAfter(addYearsToCivilDateString(todayDateString, -YEARLY_RANGE_YEARS))
    );
  } else {
    chartData[chartTimeRange.YEARLY] = { ...dailyData };
  }

  // add one year ago data points to yearly data
  const oneYearAgoData = responseData[chartTimeRange.ONE_YEAR_AGO];
  const yearlyNetWorthData = chartData[chartTimeRange.YEARLY][dataPointsKey];
  if (yearlyNetWorthData.length && oneYearAgoData) {
    // Normalize dates to midnight to avoid timezone issues
    const firstDataDate = parseKuberaDateString(yearlyNetWorthData[0].date);
    const oneYearAgoDate = parseKuberaDateString(oneYearAgoData.date);

    if (firstDataDate.getTime() !== oneYearAgoDate.getTime()) {
      chartData[chartTimeRange.YEARLY][dataPointsKey] = [oneYearAgoData, ...yearlyNetWorthData];
    }
  }

  const getOldestDataPoint = () => {
    const firstDataPoints = [dailyData, weeklyData, monthlyData, yearlyData]
      .map(bucket => bucket?.[dataPointsKey]?.[0])
      .filter(Boolean);

    return firstDataPoints.length
      ? firstDataPoints.reduce((oldest, current) =>
          toCivilDateString(current.date) < toCivilDateString(oldest.date) ? current : oldest
        )
      : null;
  };

  // Calculate all data
  if (monthlyData?.[dataPointsKey]?.length) {
    const networthChartStartDateString =
      !networthChartStartDate === true ? null : getKuberaDateString(networthChartStartDate);
    const oldestDataPointFromResponse = getOldestDataPoint();
    const oldestDataPoint =
      !networthChartStartDateString === false &&
      oldestDataPointFromResponse &&
      toCivilDateString(oldestDataPointFromResponse.date) > networthChartStartDateString
        ? oldestDataPointFromResponse
        : { date: networthChartStartDateString };

    const timeSinceFirstDataPoint = getCivilDaysBetween(oldestDataPoint?.date, todayDateString) * ONE_DAY_IN_MILLIS;
    if (timeSinceFirstDataPoint <= ALL_RANGE_DAILY_MAX_AGE) {
      chartData[chartTimeRange.ALL] = { ...dailyData };
    } else if (timeSinceFirstDataPoint <= ALL_RANGE_WEEKLY_MAX_AGE) {
      chartData[chartTimeRange.ALL] = { ...weeklyData };
    } else if (timeSinceFirstDataPoint <= ALL_RANGE_MONTHLY_MAX_AGE) {
      chartData[chartTimeRange.ALL] = { ...monthlyData };
    } else {
      chartData[chartTimeRange.ALL] = { ...yearlyData };
      chartData[chartTimeRange.ALL][dataPointsKey] = [...yearlyData[dataPointsKey]].sort((a, b) =>
        getCivilDaysBetween(b.date, a.date)
      );
    }
  } else {
    chartData[chartTimeRange.ALL] = { ...dailyData };
  }

  if (responseData[chartTimeRange.LAST_YEAR_END]) {
    chartData[chartTimeRange.LAST_YEAR_END] = responseData[chartTimeRange.LAST_YEAR_END];
  }
  chartData[chartTimeRangeGroups.GROUP_BY_DAY] = { ...responseData[chartTimeRangeGroups.GROUP_BY_DAY] };
  chartData[chartTimeRangeGroups.GROUP_BY_WEEK] = { ...responseData[chartTimeRangeGroups.GROUP_BY_WEEK] };
  chartData[chartTimeRangeGroups.GROUP_BY_MONTH] = { ...responseData[chartTimeRangeGroups.GROUP_BY_MONTH] };
  chartData[chartTimeRangeGroups.GROUP_BY_YEAR] = { ...responseData[chartTimeRangeGroups.GROUP_BY_YEAR] };
  if (isReportsData) {
    chartData.custodiansFundData = responseData.custodiansFundData;
    chartData.tickerMcapList2 = responseData.tickerMcapList2;
    chartData.stockSectors = responseData.stockSectors;
    chartData.cryptoSectors = responseData.cryptoSectors;
    chartData.tickerRegionList = responseData.tickerRegionList;
    chartData.primary = responseData.primary;
    chartData.directLinkedAssetCustodianId = responseData.directLinkedAssetCustodianId;
    chartData.directLinkedDebtCustodianId = responseData.directLinkedDebtCustodianId;
    chartData.directLinkedAssetCustodianName = responseData.directLinkedAssetCustodianName;
    chartData.directLinkedDebtCustodianName = responseData.directLinkedDebtCustodianName;
    chartData.directLinkedAssetCustodianSectionId = responseData.directLinkedAssetCustodianSectionId;
    chartData.directLinkedDebtCustodianSectionId = responseData.directLinkedDebtCustodianSectionId;
    chartData.directLinkedAssetCustodianOwnership = responseData.directLinkedAssetCustodianOwnership;
    chartData.directLinkedDebtCustodianOwnership = responseData.directLinkedDebtCustodianOwnership;
    chartData.linkedAssetCustodianOwnershipHistory = responseData.linkedAssetCustodianOwnershipHistory;
    chartData.linkedDebtCustodianOwnershipHistory = responseData.linkedDebtCustodianOwnershipHistory;
    chartData.linkedPortfolioId = responseData.linkedPortfolioId;
    chartData.parentPortfolioId = responseData.parentPortfolioId;
    chartData.portfolioId = responseData.portfolioId;
    chartData.linkedAssetCustodianIdInCurrentPortfolio = responseData.linkedAssetCustodianIdInCurrentPortfolio;
    chartData.linkedDebtCustodianIdInCurrentPortfolio = responseData.linkedDebtCustodianIdInCurrentPortfolio;
  }
  return chartData;
};

export const getYTDCustodianChartData = (chartData, lastYearEndData, portfolioStartDate) => {
  let ytdCustodianChartData;
  let baseChartData;
  const ytdStartDate = getYTDStartDate();
  const ytdStartDateString = getYTDStartDateString();
  if (portfolioStartDate.getTime() > ytdStartDate.getTime()) {
    const ytdIndex = getYTDIndexInTimeRangeArray();
    return chartData.chart[timeRanges[ytdIndex - 1]];
  } else {
    const dateDifferenceFromYTD = getCurrentDateDifferenceFromYTD();
    if (dateDifferenceFromYTD <= 7) {
      baseChartData = chartData.chart[chartTimeRange.WEEKLY];
    } else if (dateDifferenceFromYTD > 7 && dateDifferenceFromYTD <= 30) {
      baseChartData = chartData.chart[chartTimeRange.MONTHLY];
    } else if (dateDifferenceFromYTD > 30 && dateDifferenceFromYTD <= 90) {
      baseChartData = chartData.chart[chartTimeRange.QUARTERLY];
    } else if (dateDifferenceFromYTD > 90) {
      baseChartData = chartData.chart[chartTimeRange.YEARLY];
    }
    if (lastYearEndData && lastYearEndData.date) {
      // find index of immediate date greater than or equal to ytd start date
      const ytdActualStartDateIndex = baseChartData.data.findIndex(
        chartData => toCivilDateString(chartData.date) >= ytdStartDateString
      );
      ytdCustodianChartData = ytdActualStartDateIndex !== -1 ? baseChartData.data.slice(ytdActualStartDateIndex) : [];
      const firstYtdDateString = ytdCustodianChartData.length ? toCivilDateString(ytdCustodianChartData[0].date) : null;
      if (
        lastYearEndData.value !== null &&
        !firstYtdDateString === false &&
        firstYtdDateString !== ytdStartDateString
      ) {
        lastYearEndData.date = ytdStartDateString;
        ytdCustodianChartData.unshift(lastYearEndData);
      }
    } else {
      ytdCustodianChartData = baseChartData.data;
    }
    return {
      ...baseChartData,
      data: ytdCustodianChartData
    };
  }
};

export const filterChartDataBasedOnPortfolioStartDate = chartData => {
  for (let i = 1; i < timeRanges.length; i++) {
    if (chartData.chart[timeRanges[i]].data.length < 7) {
      for (let j = i; j < timeRanges.length; j++) {
        chartData.chart[timeRanges[j]].data = chartData.chart[timeRanges[i - 1]].data;
      }
    }
  }
  return chartData;
};

export const getSymbolForTickerUsingShortName = (state, shortName) => {
  const ticker = getTickerUsingShortName(state, shortName);

  try {
    if (ticker && ticker.symbol.length > 0) {
      return ticker.symbol[0];
    }
  } catch (e) {
    captureError(state, `No ticker symbol for ${shortName}. Error: ${e?.message || e}`);
  }
  return shortName;
};

export const isCryptoCurrency = (state, currency) => {
  const ticker = getTickerUsingShortName(state, currency);
  return ticker && ticker.type === tickerTypes.CRYPTO;
};

export const formatNumberWithCurrency = (
  state,
  number,
  currencyCode,
  showCurrencyAsSymbol = true,
  maxFractionDigits = 2,
  showFullCode = false,
  alwaysShowDecimal = false,
  showCurrency = true,
  useGrouping = true,
  compactWidth = false,
  hidePvst = false,
  roundDownIfNeeded = false,
  treatAsCrypto = false,
  skipFiatDecimalClamp = false
) => {
  if (number === null || number === undefined || !currencyCode === true) {
    return "";
  }

  var localizedNumber = parseFloat(number);
  if (isNaN(localizedNumber)) {
    return number;
  }

  const ticker = getTickerUsingShortName(state, currencyCode);
  if (!ticker === true) {
    return "";
  }

  var currencyString = "";
  var isShowingSymbol = false;
  if (showCurrency) {
    const currencySymbol = getSymbolForTickerUsingShortName(state, currencyCode);
    currencyString = (compactWidth ? shortenTickerIfNeeded(state, ticker.code) : ticker.code) + " ";
    if (showCurrencyAsSymbol === true && currencySymbol !== currencyCode) {
      currencyString = currencySymbol;
      isShowingSymbol = true;
    } else if (showFullCode === true) {
      currencyString = ticker.shortName + " ";
    }
  }
  const isNegative = localizedNumber < 0;
  if (compactWidth) {
    const numDigitsBeforeDecimal = Math.floor(Math.log10(Math.abs(localizedNumber))) + 1;

    if (numDigitsBeforeDecimal + currencyString.length - 1 > 8) {
      const sixMostSig = localizedNumber - (localizedNumber % Math.pow(10, Math.max(numDigitsBeforeDecimal, 6) - 6));
      const displayString = shortFormatNumberWithCurrency(
        state,
        sixMostSig,
        currencyCode,
        true,
        true,
        false,
        alwaysShowDecimal,
        false,
        showCurrencyAsSymbol
      );
      return hidePvst ? displayString.replace(/PVST\s?/, "#") : displayString;
    }
  }

  const isFiatCurrency = isFiatTicker(ticker);
  let resolvedMaxFractionDigits = maxFractionDigits;
  if (isCryptoCurrency(state, currencyCode) && maxFractionDigits === 2) {
    // do dynamic rounding for crypto
    // logic matches shortFormatNumberWithCurrency
    const absoluteValue = Math.abs(localizedNumber);
    if (absoluteValue === 0 || !isFinite(absoluteValue)) {
      resolvedMaxFractionDigits = 4;
    } else {
      const numDigitsBeforeDecimal = Math.floor(Math.log10(absoluteValue)) + 1;
      resolvedMaxFractionDigits = numDigitsBeforeDecimal > 2 ? Math.max(0, 7 - numDigitsBeforeDecimal) : 4;
    }
  }
  let maxFraction = isFiatCurrency && skipFiatDecimalClamp !== true ? 2 : resolvedMaxFractionDigits ?? 2;
  if (treatAsCrypto === true) {
    maxFraction = 4;
  }
  const minFractionDigits = treatAsCrypto === true ? 4 : alwaysShowDecimal === true ? 2 : undefined;

  if (roundDownIfNeeded === true) {
    localizedNumber =
      isCryptoCurrency(state, currencyCode) === true ||
      (localizedNumber > 0 && localizedNumber < 1) ||
      (localizedNumber < 0 && localizedNumber > -1)
        ? localizedNumber
        : Math.kuberaFloor(localizedNumber);
  }

  if (SIMILAR_FORMAT_CURRENCIES.get("INR").includes(currencyCode) && getCharacterForNumberSeparator(state) === ",") {
    localizedNumber = localizedNumber.toLocaleString("en-IN", {
      maximumFractionDigits: maxFraction,
      minimumFractionDigits: minFractionDigits,
      useGrouping: useGrouping
    });
  } else {
    localizedNumber = formatNumberWithKuberaNumberFormatSettingsSelector(state, localizedNumber, {
      maximumFractionDigits: maxFraction,
      minimumFractionDigits: minFractionDigits,
      useGrouping: useGrouping
    });
  }

  if (hidePvst) {
    currencyString = currencyString.replace(/PVST\s?/, "#");
  }
  if (userMaskAllValuesSelector(state) === true) {
    return currencyString + "XXXX";
  }
  if (localizedNumber === "") {
    return number;
  }
  if (isNegative && isShowingSymbol) {
    return "-" + currencyString + localizedNumber.replace("-", "");
  }
  return currencyString + localizedNumber;
};

export const shortFormatNumberWithCurrency = (
  state,
  number,
  currencyCode,
  abbreviate = false,
  includeCurrencySymbol = false,
  includePositiveSign = false,
  alwaysShowDecimal = false,
  showZeroAsText = false,
  showCurrencyAsSymbol = true,
  compactWidth = false
) => {
  var currencySymbol =
    showCurrencyAsSymbol === false ? currencyCode : getSymbolForTickerUsingShortName(state, currencyCode);
  const isShowingSymbol = currencySymbol !== currencyCode;

  if (currencySymbol === currencyCode) {
    currencySymbol = shortenTickerIfNeeded(state, currencySymbol) + " ";
  }

  if (number === null || number === undefined) {
    return includeCurrencySymbol ? currencySymbol + 0 : 0;
  }

  if (userMaskAllValuesSelector(state) === true) {
    return includeCurrencySymbol ? currencySymbol + "XXXX" : "XXXXXX";
  }

  var localizedNumber = parseFloat(number);

  if (localizedNumber === 0 && showZeroAsText === true) {
    return includeCurrencySymbol ? currencySymbol + "Zero" : "Zero";
  }

  if (isNaN(localizedNumber)) {
    return includeCurrencySymbol ? currencySymbol + number : number;
  }

  var formattedNumberString = includeCurrencySymbol ? currencySymbol + 0 : 0;
  const isNegative = localizedNumber < 0;
  const minFractionDigits = alwaysShowDecimal === true ? 2 : undefined;

  if (compactWidth && includeCurrencySymbol && currencySymbol.length > 1) {
    const numDigitsBeforeDecimal = Math.floor(Math.log10(Math.abs(localizedNumber))) + 1;
    localizedNumber -= localizedNumber % Math.pow(10, Math.max(numDigitsBeforeDecimal - 8 + currencySymbol.length, 0));
  }

  if (SIMILAR_FORMAT_CURRENCIES.get("INR").includes(currencyCode)) {
    if (Math.abs(localizedNumber) >= 10_000_000) {
      formattedNumberString =
        parseFloat((localizedNumber / 10_000_000).toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: minFractionDigits
        }) + (abbreviate === false ? " Crore" : "Cr");
    } else if (Math.abs(localizedNumber) >= 100_000) {
      formattedNumberString =
        parseFloat((localizedNumber / 100_000).toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: minFractionDigits
        }) + (abbreviate === false ? " Lakh" : "L");
    } else {
      formattedNumberString = `${
        localizedNumber > 0 && localizedNumber < 1
          ? localizedNumber.toFixed(2)
          : Math.kuberaFloor(localizedNumber).toLocaleString()
      }`;
    }
  } else {
    if (Math.abs(localizedNumber) >= 1_000_000_000_000) {
      let currencyString = abbreviate === false ? " Trillion" : "T";
      if (SIMILAR_FORMAT_CURRENCIES.get("COP").includes(currencyCode)) {
        currencyString = abbreviate === false ? " Billion" : "B";
      }
      formattedNumberString =
        formatNumberWithKuberaNumberFormatSettingsSelector(
          state,
          parseFloat((localizedNumber / 1_000_000_000_000).toFixed(3)),
          {
            minimumFractionDigits: minFractionDigits
          }
        ) + currencyString;
    } else if (
      Math.abs(localizedNumber) >= 1_000_000_000 &&
      !SIMILAR_FORMAT_CURRENCIES.get("COP").includes(currencyCode)
    ) {
      formattedNumberString =
        formatNumberWithKuberaNumberFormatSettingsSelector(
          state,
          parseFloat((localizedNumber / 1_000_000_000).toFixed(3)),
          {
            minimumFractionDigits: minFractionDigits
          }
        ) + (abbreviate === false ? " Billion" : "B");
    } else if (Math.abs(localizedNumber) >= 1_000_000) {
      const longNumLongSymbol =
        includeCurrencySymbol && currencySymbol.length >= 3 && Math.abs(localizedNumber) > 99_999_999;
      let decimalDigits = longNumLongSymbol ? 2 : 3;
      if (Math.abs(localizedNumber) >= 1_000_000_000) {
        const logValue = Math.log10(Math.abs(number));
        decimalDigits = Math.max((longNumLongSymbol ? 10 : 11) - Math.floor(logValue), 0);
      }
      formattedNumberString =
        formatNumberWithKuberaNumberFormatSettingsSelector(
          state,
          parseFloat((localizedNumber / 1_000_000).toFixed(decimalDigits)),
          {
            minimumFractionDigits: minFractionDigits
          }
        ) + (abbreviate === false ? " Million" : "M");
    } else {
      if (isCryptoCurrency(state, currencyCode)) {
        const numDigitsBeforeDecimal = Math.floor(Math.log10(Math.abs(localizedNumber))) + 1;
        let maximumFractionDigits = 4;
        if (numDigitsBeforeDecimal > 2) {
          maximumFractionDigits = Math.max(0, 7 - numDigitsBeforeDecimal);
        }

        formattedNumberString = formatNumberWithKuberaNumberFormatSettingsSelector(state, localizedNumber, {
          maximumFractionDigits
        });
      } else {
        formattedNumberString = `${
          localizedNumber > 0 && localizedNumber < 1
            ? localizedNumber.toFixed(2)
            : formatNumberWithKuberaNumberFormatSettingsSelector(state, Math.kuberaFloor(localizedNumber))
        }`;
      }
    }
  }

  if (includeCurrencySymbol) {
    if (isNegative && isShowingSymbol) {
      return "-" + currencySymbol + formattedNumberString.replace("-", "");
    }
    return currencySymbol + formattedNumberString;
  }
  return includePositiveSign && !isNegative ? `+${formattedNumberString}` : formattedNumberString;
};

// Ticker rates are units-per-USD, so 1/rate is the price in USD.
export const getFormattedTickerPrice = (state, rate, currency = "USD") => {
  if (!rate) return shortFormatNumberWithCurrency(state, 0, currency || "USD");
  const priceInUsd = 1 / rate;
  const usdToCurrencyRate = currency && currency !== "USD" ? getExchangeRate(state, "USD", currency) : 0;
  if (!usdToCurrencyRate) return formatNumberWithCurrency(state, priceInUsd, "USD");
  return formatNumberWithCurrency(state, priceInUsd * usdToCurrencyRate, currency);
};

export const updateCustodianDetailsAction = updatedDetails => ({
  type: UPDATE_CUSTODIAN_DETAILS,
  updatedDetails
});

export const shortenTickerIfNeeded = (state, ticker) => {
  let final = ticker;
  const tickerVal = getTickerUsingShortName(state, ticker);
  if (tickerVal && tickerVal.code !== "UNKNOWN") {
    final = tickerVal.code;
  }
  return final === "UNKNOWN" ? "????" : final.length > 5 ? "*" + final.slice(final.length - 4, final.length) : final;
};

export const findPortfolioInTree = (node, targetId) => {
  if (node?.id === targetId) {
    return node;
  }
  for (const child of node?.children || []) {
    const found = findPortfolioInTree(child, targetId);
    if (found) {
      return found;
    }
  }
  return null;
};

export const buildGlobalClubbingNames = (state, node, portfolioMap, globalClubbingNames = new Map()) => {
  if (node?.id) {
    const portfolioData = portfolioMap.get(node.id);
    if (!portfolioData) {
      return globalClubbingNames;
    }

    for (const custodian of portfolioData?.details?.custodian || []) {
      if (custodian.linkType === accountLinkingService.KUBERA_PORTFOLIO) {
        continue;
      }
      const tickerDetails = getTickerUsingId(state, custodian.valueTickerId);
      if (tickerDetails.type !== "fiat") {
        if (!globalClubbingNames.has(tickerDetails.id)) {
          globalClubbingNames.set(tickerDetails.id, custodian.name);
        } else {
          globalClubbingNames.set(tickerDetails.id, tickerDetails.name);
        }
      }
    }
  }
  for (const child of node?.children || []) {
    buildGlobalClubbingNames(state, child, portfolioMap, globalClubbingNames);
  }
  return globalClubbingNames;
};

export const getClubbingKeyForCustodian = (tickerDetails, displayName, globalClubbingNames) => {
  if (!tickerDetails || tickerDetails.type === "fiat" || tickerDetails.shortName === UNKNOWN_TICKER_SHORT_NAME) {
    return displayName;
  }
  const clubbingName = globalClubbingNames?.get(tickerDetails.id) || displayName;
  return `${getTickerSymbolToPrefix(tickerDetails)} • ${clubbingName}`;
};

export const getCurrentDateDifferenceFromYTD = () =>
  getCivilDaysBetween(getYTDStartDateString(), getTodayCivilDateString()) + DAYS_ELAPSED_INCLUSIVE_OFFSET;

export const getCurrentDateDifferenceFromQTD = () =>
  getCivilDaysBetween(getQTDStartDateString(), getTodayCivilDateString()) + DAYS_ELAPSED_INCLUSIVE_OFFSET;

export const getYTDIndexInTimeRangeArray = () => {
  const dateDifferenceFromYTD = getCurrentDateDifferenceFromYTD();
  let ytdIndex;
  if (dateDifferenceFromYTD <= 7) {
    ytdIndex = 1;
  } else if (dateDifferenceFromYTD > 7 && dateDifferenceFromYTD <= 30) {
    ytdIndex = 2;
  } else if (dateDifferenceFromYTD > 30 && dateDifferenceFromYTD <= 90) {
    ytdIndex = 3;
  } else if (dateDifferenceFromYTD > 90 && dateDifferenceFromYTD <= 365) {
    ytdIndex = 4;
  } else {
    ytdIndex = 5;
  }
  return ytdIndex;
};

export const userMaskAllValuesSelector = state => {
  const siteConfig = siteConfigSelector(state);
  if (siteConfig && siteConfig.mask === 1) {
    return state.auth.shareMaskValue;
  }
  return isMobileDevice ? state.auth.userPreferences.maskAllValuesMobile : state.auth.userPreferences.maskAllValues;
};

export const fundScheduleTypes = {
  CAPITAL_CALL: "capitalcall",
  DISTRIBUTION: "distribution"
};

export const fundScheduleDurations = {
  OVERDUE: "overdue",
  IN_30_DAYS: "in30days",
  IN_90_DAYS: "in90days",
  MORE_THAN_90_DAYS: "morethan90days",
  UNSCHEDULED: "unscheduled"
};

export const getFundScheduleTitle = duration => {
  switch (duration) {
    case fundScheduleDurations.OVERDUE:
      return i18n.t("fundScheduleDuration.overdue");
    case fundScheduleDurations.IN_30_DAYS:
      return i18n.t("fundScheduleDuration.30days");
    case fundScheduleDurations.IN_90_DAYS:
      return i18n.t("fundScheduleDuration.90days");
    case fundScheduleDurations.MORE_THAN_90_DAYS:
      return i18n.t("fundScheduleDuration.moreThan90Days");
    case fundScheduleDurations.UNSCHEDULED:
      return i18n.t("fundScheduleDuration.unscheduled");
    default:
      "";
  }
};

export const isWlEmailPlaceholder = email => {
  return email.indexOf(WL_PLACEHOLDER_EMAIL) !== -1 && email.endsWith("@kubera.com");
};

export const assetClassesListSelector = createSelector(
  [state => state.tickers.tickerData],
  tickerData => tickerData?.assetClasses || []
);

export const REAL_ESTATE_ASSET_CLASS_INDEX = 6;

export const getIrrKeyForTimePeriod = timePeriod => {
  switch (timePeriod) {
    case irrTimePeriods.YTD:
      return "ytd";
    case irrTimePeriods.QTD:
      return "qtd";
    case irrTimePeriods.YEAR:
      return "year";
    default:
      return "all";
  }
};

export const getIrrLabelForTimePeriod = timePeriod => {
  if (timePeriod === irrTimePeriods.YTD) {
    return i18n.t("ytdChange");
  } else if (timePeriod === irrTimePeriods.QTD) {
    return i18n.t("qtdChange");
  } else if (timePeriod === irrTimePeriods.YEAR) {
    return "1Y";
  }
  return "";
};

export const getIrrPeriodLabel = selectedTimePeriod => {
  const irrLabelForTimePeriod = getIrrLabelForTimePeriod(selectedTimePeriod);
  return irrLabelForTimePeriod ? ` • ${irrLabelForTimePeriod}` : "";
};

export const resolveWhenCounterReachesZero = (noOfItems = 0) => {
  const promise = new DeferredPromise();
  let counter = noOfItems;

  return [
    {
      set counter(value) {
        counter = value;
        if (counter === 0) {
          promise.resolve();
        }
      },
      get counter() {
        return counter;
      }
    },
    promise
  ];
};

export const getChartRangeFromTimePeriod = timePeriod => {
  if (timePeriod === irrTimePeriods.START_DATE) {
    return chartTimeRange.ALL;
  } else if (timePeriod === irrTimePeriods.YEAR) {
    return chartTimeRange.YEARLY;
  } else if (timePeriod === irrTimePeriods.QTD) {
    return chartTimeRange.QTD;
  } else if (timePeriod === irrTimePeriods.YTD) {
    return chartTimeRange.YTD;
  }
  return null;
};

export const updatePortfolioAction = (portfolio, propToBeUpdated) => ({
  type: UPDATE_PORTFOLIO,
  portfolio,
  propToBeUpdated
});

export const tickerAdditionalMetaDataSelector = createSelector(
  [state => state.tickers.tickerData],
  tickerData => tickerData?.tickerAddlMeta || []
);
